import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { initGA } from './lib/analytics';
import { ErrorBoundary } from './components/ErrorBoundary';

// Immediately initialize analytics on DOM load so early interactions/pageviews are captured
initGA();

// Global handler to catch and prevent unhandled promise rejections from automated crawler stubs
// (e.g., Googlebot WRS wrsParams.serviceWorkers intentionally rejecting navigator.serviceWorker.register)
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    const msg = String(reason?.message || reason || '');
    if (
      msg.includes('wrsParams') ||
      msg.includes('serviceWorkers.navigator.serviceWorker') ||
      (msg.includes('Rejected') && msg.includes('serviceWorker'))
    ) {
      event.preventDefault();
    }
  });

  // Global uncaught error listener to prevent blank screen in critical edge cases
  window.addEventListener('error', (event) => {
    // Avoid interfering with extension or resize observer errors
    if (event.message?.includes('ResizeObserver') || event.message?.includes('Script error')) {
      return;
    }
  });
}

import App from './App.tsx';
import './index.css';

if (typeof window !== 'undefined') {
  let dragPolyfillLoaded = false;
  const initDragPolyfill = async () => {
    if (dragPolyfillLoaded) return;
    dragPolyfillLoaded = true;
    try {
      const [{ polyfill }, { scrollBehaviourDragImageTranslateOverride }] = await Promise.all([
        import('mobile-drag-drop'),
        import('mobile-drag-drop/scroll-behaviour'),
        import('mobile-drag-drop/default.css'),
      ]);
      polyfill({
        dragImageTranslateOverride: scrollBehaviourDragImageTranslateOverride,
      });
    } catch {}
  };
  window.addEventListener('touchstart', initDragPolyfill, { passive: true, once: true });
}

import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { PiPProvider } from './lib/PiPContext';

const rootElement = document.getElementById('root');

if (rootElement) {
  createRoot(rootElement).render(
    <StrictMode>
      <ErrorBoundary>
        <HelmetProvider>
          <BrowserRouter>
            <PiPProvider>
              <App />
            </PiPProvider>
          </BrowserRouter>
        </HelmetProvider>
      </ErrorBoundary>
    </StrictMode>
  );
}
